import { useState } from "react";
import { useSettings } from "@/stores/settingsStore";
import { WEAPON_ORDER, WEAPONS } from "@/game/weapons/weapons";
import { useT } from "@/i18n";
import { useUiSounds } from "@/lib/audio";
import LobbyLayout from "@/components/LobbyLayout";
import { SCREEN } from "@/stores/gameStore";
import { toast } from "sonner";
import { Save, Layers } from "lucide-react";

// Beta stubs for melee + grenade catalogues so the slot picker is not empty.
const MELEE = [
    { id: "knife", displayName: "COMBAT KNIFE", desc: "Silent · Fast" },
    { id: "baton", displayName: "TACTICAL BATON", desc: "Blunt · Reach" },
];
const GRENADES = [
    { id: "frag", displayName: "FRAG", desc: "Lethal · 5m" },
    { id: "smoke", displayName: "SMOKE", desc: "Concealment" },
    { id: "flash", displayName: "FLASH", desc: "Disorient" },
];

export default function LoadoutScreen() {
    const t = useT();
    const sfx = useUiSounds();
    const loadout = useSettings((s) => s.weaponLoadout);
    const update = useSettings((s) => s.update);
    const [draft, setDraft] = useState(loadout);

    const set = (slot, id) => setDraft({ ...draft, [slot]: id });

    const save = () => {
        sfx.click();
        update({ weaponLoadout: draft });
        toast.success(t("loadout.saved"));
    };

    return (
        <LobbyLayout active={SCREEN.LOADOUT}>
            <div data-testid="loadout-screen" className="w-full h-full overflow-y-auto px-8 py-8">
                <div className="max-w-6xl mx-auto">
                    <div className="mb-6">
                        <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/80">
                            {t("loadout.subtitle")}
                        </div>
                        <div className="axis-heading text-3xl tracking-[0.4em] text-white flex items-center gap-3">
                            <Layers size={22} className="text-orange-400" />
                            {t("loadout.title")}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <Slot label={t("loadout.slot_primary")} value={draft.primary} options={WEAPON_ORDER.map(id => WEAPONS[id])} onChange={(v) => set("primary", v)} accent="#00e5ff" />
                        <Slot label={t("loadout.slot_secondary")} value={draft.secondary} options={WEAPON_ORDER.map(id => WEAPONS[id])} onChange={(v) => set("secondary", v)} accent="#c084fc" />
                        <Slot label={t("loadout.slot_pistol_start")} value={draft.pistol} options={[WEAPONS.pistol]} onChange={(v) => set("pistol", v)} accent="#ffb300" />
                        <Slot label={t("loadout.slot_melee")} value={draft.melee} options={MELEE} onChange={(v) => set("melee", v)} accent="#ff7043" />
                        <Slot label={t("loadout.slot_grenade")} value={draft.grenade} options={GRENADES} onChange={(v) => set("grenade", v)} accent="#00ff88" />
                    </div>

                    <div className="mt-8 flex justify-end">
                        <button
                            data-testid="loadout-save"
                            onClick={save}
                            className="axis-btn axis-cut-sm px-8 py-3 border border-orange-400 bg-orange-500/20 text-orange-200 hover:bg-orange-500/40 hover:text-white transition flex items-center gap-2"
                        >
                            <Save size={15} /> {t("loadout.save")}
                        </button>
                    </div>
                </div>
            </div>
        </LobbyLayout>
    );
}

function Slot({ label, value, options, onChange, accent }) {
    return (
        <div className="axis-glass axis-cut-sm px-5 py-4 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-1 h-6" style={{ background: accent }} />
                <span className="axis-mono text-[11px] tracking-[0.4em] text-white/70">
                    {label}
                </span>
            </div>
            <div className="flex flex-wrap gap-2">
                {options.map((o) => {
                    const active = o.id === value;
                    return (
                        <button
                            key={o.id}
                            data-testid={`loadout-option-${o.id}`}
                            onClick={() => onChange(o.id)}
                            className={`axis-cut-sm px-4 py-2 border transition text-left min-w-[180px] ${
                                active
                                    ? "bg-white/10 text-white"
                                    : "border-white/10 bg-black/30 text-white/70 hover:text-white hover:bg-white/5"
                            }`}
                            style={{ borderColor: active ? accent : undefined }}
                        >
                            <div className="axis-heading text-xs tracking-widest">
                                {o.displayName}
                            </div>
                            <div className="axis-mono text-[9px] tracking-[0.3em] text-white/40 mt-0.5">
                                {o.desc || (o.damage ? `${o.damage} DMG · ${o.range}m` : "")}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
